/**
 * Consent-gated Sentry Session Replay (GitLab #649, D9).
 *
 * Replay records what the customer sees and does. On these journeys that is
 * a person in financial difficulty typing their income, their debts and their
 * bank details, so it is treated as non-essential processing: nothing is
 * recorded until the analytics category is granted, and the recorder is not
 * merely muted beforehand — the integration is never added, so no replay
 * session is written to browser storage either.
 *
 * `instrumentation-client.ts` therefore inits with no replay integration and
 * this module adds it on the consent signal. Sampling is unchanged: the rates
 * are read from the client options when the integration is added, so consent
 * decides who is eligible and `replaysSessionSampleRate` still decides which
 * eligible sessions are kept.
 *
 * Masking is applied here rather than at init for the same reason — the
 * options travel with the integration, so there is no configuration in which
 * replay can start without them.
 */

import * as Sentry from '@sentry/nextjs';

/**
 * Derived from the SDK rather than imported: `@sentry/nextjs` re-exports the
 * browser integration values but not the `Integration` type, and pulling it
 * from `@sentry/core` would pin us to a transitive package.
 */
type ReplayIntegration = ReturnType<typeof Sentry.replayIntegration>;

/**
 * Masking is on for everything, not tuned per field.
 *
 * An allowlist would mean re-deciding, every time a form gains a field,
 * whether that field is safe to film. `maskAllText` covers text nodes and
 * `maskAllInputs` covers what is typed; `blockAllMedia` keeps uploaded
 * documents — bank statements, benefit letters — out of the recording.
 */
export const REPLAY_PRIVACY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
} as const;

/**
 * Seam for tests, which have no initialised SDK. Structural rather than
 * `typeof Sentry.x` so a fake need only provide what is used here.
 */
export interface SentryReplayDeps {
  getClient: () => { addIntegration: (integration: ReplayIntegration) => void } | undefined;
  replayIntegration: (options: typeof REPLAY_PRIVACY_OPTIONS) => ReplayIntegration;
  getReplay: () => { stop: () => unknown } | undefined;
}

const defaultDeps: SentryReplayDeps = {
  getClient: () => Sentry.getClient(),
  replayIntegration: (options) => Sentry.replayIntegration(options),
  getReplay: () => Sentry.getReplay(),
};

type ReplayState = 'idle' | 'recording' | 'stopped';

let state: ReplayState = 'idle';

/**
 * Start or stop replay to match the analytics consent decision.
 *
 * Once stopped it stays stopped for the rest of the page load. Restarting
 * would have to call `start()` directly, which ignores the sample rate and
 * records the session in full — recording *more* of a customer who has just
 * been changing their privacy settings is the wrong way to resolve the
 * ambiguity. A re-grant takes effect on their next page load, where normal
 * sampling applies. In practice both transitions usually reload anyway,
 * because the GTM tag set has to change with them.
 *
 * Never throws. A failure in telemetry must not take a customer's journey
 * with it.
 */
export function syncSentryReplayConsent(
  granted: boolean,
  deps: SentryReplayDeps = defaultDeps,
): void {
  try {
    // No client means the SDK never initialised — no DSN, or a test
    // environment. There is nothing to gate.
    const client = deps.getClient();
    if (!client) return;

    if (granted) {
      if (state !== 'idle') return;
      client.addIntegration(deps.replayIntegration(REPLAY_PRIVACY_OPTIONS));
      state = 'recording';
      return;
    }

    if (state !== 'recording') return;
    state = 'stopped';
    // `stop()` flushes what has been buffered and discards the session.
    void Promise.resolve(deps.getReplay()?.stop()).catch(() => {});
  } catch {
    // Swallowed on purpose: see the note above.
  }
}

/** Test-only. The state above is module-level and outlives a single test. */
export function resetSentryReplayConsentState(): void {
  state = 'idle';
}
